import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
} from '@material-ui/core';
import { useAsync, useAsyncFn } from 'react-use';
import { useSelector } from 'react-redux';
import { Skeleton } from '@material-ui/lab';
import { createSelector } from 'reselect';
import { Trans, useTranslation } from 'react-i18next';

import {
  confirmAdminSession,
  deleteAdminSession,
  initAdminSession,
} from 'services/Auth';
import { getClientCode, getUserRights } from 'reducers/Login';
import { deleteEntireErplyAccount } from 'services/AccountAdmin';
import Loader from 'components/Loader';
import { useAppDispatch } from 'reducers';
import { hardLogout } from 'actions/Login';

export function DeleteAccount() {
  const [deletion, deleteAccount] = useAsyncFn(async () => {
    await deleteEntireErplyAccount();
    // Admin session no longer required so close it for security
    // Don't await for this because we don't need the delay, nor do we want to falsely report
    // that the account failed to delete when it's only the session deletion that had an error
    deleteAdminSession();
    return true;
  });
  const clientCode = useSelector(getClientCode);
  const dispatch = useAppDispatch();
  const { t } = useTranslation('deleteAccount');
  if (deletion.value) {
    return (
      <>
        <Typography>{t('deleteSuccess', { clientCode })}</Typography>
        <Box marginY={2}>
          <Button
            onClick={() => dispatch(hardLogout())}
            variant="contained"
            color="secondary"
          >
            {t('returnToHome')}
          </Button>
        </Box>
      </>
    );
  }
  return (
    <>
      <Trans
        ns="deleteAccount"
        i18nKey="confirmation.warning"
        components={{
          Line: <Typography />,
        }}
      />
      <WithAuthenticatedSession>
        <WithConfirmClientcode>
          <Loader loadingText={t('deleting')} show={deletion.loading} block>
            <Box marginY={2}>
              <Button
                color="primary"
                fullWidth
                disabled={deletion.loading}
                variant="contained"
                onClick={deleteAccount}
              >
                {t('confirmation.button')}
              </Button>
            </Box>
          </Loader>
          <Typography color="error">{deletion.error?.message}</Typography>
        </WithConfirmClientcode>
      </WithAuthenticatedSession>
    </>
  );
}

function WithConfirmClientcode({ children }) {
  const [inputClientCode, setInputClientCode] = useState('');
  const clientCodeMatches =
    inputClientCode === String(useSelector(getClientCode));

  const { t } = useTranslation('deleteAccount');
  return (
    <>
      <Box height="2em" />
      <Typography>{t('confirmClientcode.prompt')}</Typography>
      <TextField
        fullWidth
        disabled={clientCodeMatches}
        variant="outlined"
        value={inputClientCode}
        onChange={e => setInputClientCode(e.target.value)}
      />
      {clientCodeMatches ? children : null}
    </>
  );
}

function WithAuthenticatedSession({ children }) {
  const adminRequest = useAsync(() => {
    return initAdminSession();
  }, []);
  const [key, setKey] = useState('');
  const [adminRequest2, confirm] = useAsyncFn(async () => {
    if (!adminRequest.value) throw new Error('Not ready to submit yet');
    const {
      value: { token },
    } = adminRequest;
    return confirmAdminSession({ key, token });
  }, [adminRequest.value, key]);

  const { t } = useTranslation('deleteAccount');

  if (adminRequest2.value) return children;

  if (!adminRequest.value) {
    return <Typography>{t('getAdminSession.loading')}</Typography>;
  }
  return (
    <>
      <Typography color="error">{adminRequest2.error?.message}</Typography>
      <Typography>
        {adminRequest.value?.channel ? (
          t('getAdminSession.prompt', { context: adminRequest.value?.channel })
        ) : (
          <Skeleton variant="rect" height="1em" width="100%" />
        )}
      </Typography>
      {(function() {
        if (adminRequest2.loading) {
          return <Skeleton variant="rect" height="4em" width="100%" />;
        }
        return (
          <Box display="flex" alignItems="stretch" style={{gap: 8}}>
            <Box flexGrow={1}>
              <TextField
                variant="outlined"
                type="password"
                disabled={adminRequest2.loading || !!adminRequest2.value}
                fullWidth
                value={key}
                onChange={e => setKey(e.target.value)}
              />
            </Box>
            <Box>
              <Button
                style={{height: '100%'}}
                onClick={confirm}
                disabled={adminRequest2.loading}
                variant="contained"
                color="primary"
              >
                {t('getAdminSession.button')}
              </Button>
            </Box>
          </Box>
        );
      })()}
    </>
  );
}

export const getUserHasDeletePerms = createSelector(
  getUserRights,
  userRights => {
    const editUsers = userRights.modules.usergroups?.edit ?? 0;
    const editConfs = userRights.modules.confs?.view ?? 0;
    return 0 < editUsers && 0 < editConfs; // admin right will be tested inside popup
  },
);
